import $http from '../common/$http';
import config from '../config';
import { AppStateContext, AuthContext } from '../Context';
import { clearAppLocalStorage } from '../Storage/localStorageKeys';

const SSO_LOGIN_PATH = '/oauth2/authorization/sangoma';
const SSO_MANAGE_PROFILE_PATH = '/integration/sso/manage-profile';
const SSO_CHANGE_PASSWORD_PATH = '/integration/sso/change-password';

let sessionRequest = null;
let sessionResuming = false;

// Response of the CSRF endpoint ({ headerName, parameterName, token }), used to send the token to the backend.
// Kept in memory only, so every tab (page load) gets its own for the session it runs in
let csrf = null;
let csrfRequest = null;

// Login happens on the backend (Sangoma SSO). The session is the JSESSIONID cookie the backend sets afterwards,
// the UI never sees it: $http sends it with every request (withCredentials).
const AuthService = {
  isUnauthorizedError(error) {
    return error?.response?.status === 401;
  },

  showLogin() {
    // The session is gone, so is the token that belonged to it
    csrf = null;

    AuthContext.model.setProps({ showLoginModal: true });
  },

  // The IDP account pages: both are opened in a new tab, the user stays logged in here
  getManageProfileUrl() {
    return config.api.url + SSO_MANAGE_PROFILE_PATH;
  },

  getChangePasswordUrl() {
    return config.api.url + SSO_CHANGE_PASSWORD_PATH;
  },

  // Starts the Sangoma SSO login on the backend
  getLoginUrl() {
    return config.api.url + SSO_LOGIN_PATH;
  },

  // Same as getLoginUrl, but the backend sends the user back to the current route after a successful login
  getLoginUrlToCurrentRoute() {
    const { pathname, search, hash } = window.location;
    const redirectPath = encodeURIComponent(pathname + search + hash);

    return `${AuthService.getLoginUrl()}?redirectPath=${redirectPath}`;
  },

  // Must be the first API call: it both verifies the session and returns the user. A 401 opens the login modal.
  loadSession() {
    if (!sessionRequest) {
      sessionRequest = $http.instance.api
        .get($http.instance.apiDomainURI + '/integration/sso/userinfo')
        .then(r => {
          AuthContext.model.setProps(r.data.content);

          AppStateContext.model.sessionValidated = true;
          // No route controllers are listening yet, so force the provider to update.
          AppStateContext.model.broadcast(true);

          return r;
        })
        .catch(error => {
          sessionRequest = null;

          throw error;
        });
    }

    return sessionRequest;
  },

  // Resolves with the user of the current session, or null when there is none (the 401 also opens the login modal)
  async fetchUserInfo() {
    try {
      const { data } = await $http.instance.apiWithoutGlobalHandler.get(
        $http.instance.apiDomainURI + '/integration/sso/userinfo'
      );

      return data.content;
    } catch (error) {
      if (AuthService.isUnauthorizedError(error)) {
        return null;
      }

      throw error;
    }
  },

  // The session cookie is shared by all tabs, so another tab can log out and in as someone else while this one keeps
  // showing the previous user's data. Whenever this tab becomes active again, check who the session belongs to now:
  // - nobody: the 401 opens the login modal;
  // - another user (or a tab that never had a user): reload, nothing of the previous user may stay on screen;
  // - the same user: take a fresh CSRF token (a new login is a new session, the old token is useless) and close the
  //   login modal if it was open.
  async resumeSession() {
    const wasLoginShown = AuthContext.model.showLoginModal;
    const { userId } = AuthContext.model;

    // No user and no login modal: the session of this tab is still being loaded
    if (sessionResuming || (!userId && !wasLoginShown)) {
      return;
    }

    sessionResuming = true;

    try {
      const user = await AuthService.fetchUserInfo();

      if (!user) {
        return;
      }

      if (!userId || user.userId !== userId) {
        // Not cancellable: unsaved edits belong to the previous user and could only be saved into the other account
        window.onbeforeunload = null;
        window.location.reload();

        return;
      }

      await AuthService.loadCsrfToken({ force: true });

      if (wasLoginShown) {
        AuthContext.model.setProps({ ...user, showLoginModal: false });
      }
    } catch {
      // The check could not be completed, the next activation of the tab tries again
    } finally {
      sessionResuming = false;
    }
  },

  // Resolves with the CSRF token of the current session, requesting it first if there is none yet
  loadCsrfToken({ force = false } = {}) {
    if (force) {
      csrf = null;
    }

    if (csrf) {
      return Promise.resolve(csrf.token);
    }

    if (!csrfRequest) {
      csrfRequest = $http.instance.api
        .get($http.instance.apiDomainURI + '/oauth2/csrf')
        .then(({ data }) => {
          csrf = data;

          return csrf.token;
        })
        .finally(() => {
          csrfRequest = null;
        });
    }

    return csrfRequest;
  },

  // Header the token has to be sent in, known once loadCsrfToken() has resolved
  getCsrfHeaderName() {
    return csrf?.headerName;
  },

  clearSession() {
    // Wipe app data on user log out
    clearAppLocalStorage();
    csrf = null;

    AuthContext.model.setProps({
      isAgent: false,
      email: '',
      firstName: '',
      internalUser: false,
      lastName: '',
      permissions: [],
      userId: 0,
    });

    AppStateContext.model.sessionValidated = false;
    AppStateContext.model.broadcast(true);
  },

  async signOut() {
    if (AuthContext.model.signOutInProgress) {
      return;
    }

    AuthContext.model.setProps({ signOutInProgress: true });

    // A form submit cannot send headers, so the token goes in as a form field. Read it before the session is
    // cleared; if it cannot be loaded the logout is still submitted and the backend decides.
    const token = await AuthService.loadCsrfToken().catch(() => null);

    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${$http.instance.apiDomainURI}/oauth2/logout`;

    if (token) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = csrf.parameterName;
      input.value = token;
      form.appendChild(input);
    }

    // /oauth2/logout redirects on to the Sangoma IDP's own logout page, a JS app rather than a plain
    // server redirect, so this has to be a real navigation: an XHR call can't run that page's JS and
    // would leave the IDP session alive. It bounces back to this app's own origin when done.
    AuthService.clearSession();

    document.body.appendChild(form);
    form.submit();
  },
};

// Re-check the session whenever the browser tab becomes active again
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    AuthService.resumeSession();
  }
});

export default AuthService;
